"use client";

import { Copy } from "lucide-react";
import CardContentLemma from "~/app/(private)/words/CardContentLemma";
import { DefinitionList } from "~/components/common/DefinitionDisplay";
import { Furigana } from "~/components/common/Furigana";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "~/components/ui/drawer";
import { useIsMobile } from "~/hooks/use-mobile";
import { toast } from "~/hooks/use-toast";
import useClipboard from "~/hooks/useClipboard";
import { cn } from "~/lib/utils";
import { type SelectLemma } from "~/server/db/lemmas/schema";
import { type SelectLexeme } from "~/server/db/lexemes/schema";
import { type SelectTranscript } from "~/server/db/transcripts/schema";

import CardExamplesSection from "./CardContentExamples";
import CardContentTranscript from "./CardContentTranscript";
import CardHeaderFull from "./CardHeader";

export default function CardFull({
  lexeme,
  lemma,
  transcript,
  isOpen = false,
  onOpenChange,
}: {
  lexeme?: SelectLexeme | null;
  lemma?: SelectLemma;
  transcript?: SelectTranscript | null;
  isOpen?: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();

  if (!lemma) return null;

  const text = lexeme?.text ?? lemma.text;
  const textFurigana = lexeme?.textFurigana ?? lemma.textFurigana;

  if (!isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="flex max-h-[90vh] flex-col">
          <DialogHeader className="group mb-4 flex flex-col items-center justify-center">
            <DialogTitle>
              <CardHeader text={text} textFurigana={textFurigana} />
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto">
            <CardContent
              lemma={lemma}
              transcript={transcript}
              lexeme={lexeme}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={onOpenChange}>
      <DrawerContent className="flex max-h-[90vh] flex-col">
        <DrawerHeader className="group mb-2 flex-shrink-0">
          <DrawerTitle>
            {lemma && (
              <CardHeaderFull
                lexeme={lexeme}
                lemma={lemma}
                isMobile={true}
                variant="drawer"
                transcript={transcript ?? undefined}
              />
            )}
          </DrawerTitle>
        </DrawerHeader>
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4 px-4 pb-6">
            <CardContent
              lemma={lemma}
              transcript={transcript}
              lexeme={lexeme}
            />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function CardHeader({
  text,
  textFurigana,
}: {
  text: string;
  textFurigana: string | null;
}) {
  const { copyToClipboard } = useClipboard();

  return (
    <div className="relative flex justify-center">
      <div>
        <Furigana rubyContent={textFurigana ?? text} />
        <Button
          size="icon"
          variant="ghost"
          className={cn(
            "absolute -right-8 top-1/2 h-6 w-6 -translate-y-1/2 opacity-0 transition-opacity",
            "hover:opacity-100 group-hover:opacity-100",
          )}
          onClick={(e) => {
            e.stopPropagation();
            void copyToClipboard(text).then(() =>
              toast({
                title: `${text}`,
                description: `Copied to your clipboard!`,
              }),
            );
          }}
        >
          <Copy className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function CardContent({
  lemma,
  transcript,
  lexeme,
}: {
  lemma: SelectLemma;
  transcript?: SelectTranscript | null;
  lexeme?: SelectLexeme | null;
}) {
  const { text } = lemma;
  const definitions = lemma.definitions?.en;

  const showLemma = lexeme && lexeme.text !== lemma.text;

  return (
    <>
      <div className="mb-6">
        <DefinitionList definitions={definitions ?? []} showTooltip />
      </div>
      {showLemma && <CardContentLemma className="mb-4" lemma={lemma} />}
      <CardContentTranscript className="mb-4" transcript={transcript ?? null} />
      <CardExamplesSection examples={lemma.examples} highlightWord={text} />
    </>
  );
}
